import {
  ComponentDescriptorOrArray,
  getRendererFromDescriptor,
  usePage,
  useField,
} from '@kizi/comparator-core';
import { Div, DivProps } from '@kizi/core-components';
import { HeadingWithIcon, HeadingWithIconProps } from '@kizi/shared-components';
import { ProviderResponse } from '../components/wrappers/PageLayout/CheckoutPage';
import React from 'react';
import { parseProductFieldData } from '../utils/product';

export interface CheckoutTopNotificationWrapperProps extends DivProps {
  heading: HeadingWithIconProps;
  content?: ComponentDescriptorOrArray | string;
  productFieldName: string;
  iconBackground?: string;
  registerInPeregrineEvent?: any;
}

export interface CheckoutTopNotificationProps
  extends Omit<CheckoutTopNotificationWrapperProps, 'productFieldName'> {
  iconBackground?: string;
}

const defaultIcon = {
  name: 'check',
};

export const CheckoutTopNotification: React.FC<
  CheckoutTopNotificationProps
> = ({ heading, content, iconBackground, ...restProps }) => {
  const contentWrapper =
    typeof content === 'string' ? (
      <Div dangerouslySetInnerHTML={{ __html: content }}></Div>
    ) : (
      getRendererFromDescriptor(content)
    );
  return (
    <Div
      display="flex"
      flexDirection="column"
      variant="CheckoutTopNotification"
      {...restProps}
    >
      <HeadingWithIcon
        {...heading}
        icon={heading?.icon ? heading?.icon : defaultIcon}
        iconBackground={iconBackground}
      />
      {contentWrapper}
    </Div>
  );
};

export const getDynamicField = (steps, fieldName: string) => {
  return steps?.reduce((acc, step) => {
    const emailField = step.fields.find((field) => field.name === fieldName);

    if (emailField) {
      return { ...acc, ...emailField };
    }

    return { ...acc };
  }, {});
};

export const CheckoutTopNotificationWrapper: React.FC<
  CheckoutTopNotificationWrapperProps
> = ({ productFieldName, content, title, heading, ...restProps }) => {
  const { state } = usePage();
  const dynamicFields = useField('apply.wizardSteps');
  const selectedProduct = useField(productFieldName);
  if (!selectedProduct && !title) return null;

  const selectedProductParsed = parseProductFieldData(selectedProduct?.value);
  const dynamicSteps = dynamicFields?.value;
  const email = getDynamicField(dynamicSteps, 'email')?.value || 'jouw email';
  const provider = selectedProductParsed?.provider?.name;
  const headingTitle =
    title || 'Je aanvraag is goedgekeurd door {dynamicProvider}';
  const newHeading = {
    title: headingTitle.replaceAll('{dynamicProvider}', provider),
    icon: defaultIcon,
  };

  let newContent = '';
  if (state?.providerRes) {
    const providerRes = state.providerRes as ProviderResponse;
    newContent = providerRes.orderCompletionText || '';
  }

  let contentFromProps;
  if (Array.isArray(content)) {
    contentFromProps = content.map((paragraph) => ({
      ...paragraph,
      props: {
        ...paragraph.props,
        children: paragraph.props.children
          ?.replaceAll('{dynamicProvider}', provider)
          ?.replaceAll('{dynamicEmail}', email),
      },
    }));
  }

  return (
    <CheckoutTopNotification
      {...restProps}
      heading={heading || newHeading}
      content={contentFromProps || newContent}
    />
  );
};
